import { create } from 'zustand';
import type { WardInfo } from '@/types/ward';
import { eventService, WARD_EVENTS } from '@/services/EventService';

export interface WardFilters {
  searchTerm: string;
  sortBy: 'name' | 'area' | 'population';
  sortOrder: 'asc' | 'desc';
  selectedDistrict: string | null;
  areaRange: [number, number] | null;
  populationRange: [number, number] | null;
}

interface OptimisticUpdate {
  wardId: number;
  changes: Partial<WardInfo>;
  timestamp: number;
}

interface SelectionHistoryEntry {
  wardId: number | null;
  timestamp: number;
}

interface WardState {
  // Danh sách phường
  wards: WardInfo[];
  selectedWard: WardInfo | null;
  hoveredWardId: number | null;
  
  // Place selection
  selectedPlaceAttributes: Record<string, unknown> | null;
  
  // Bộ lọc
  filters: WardFilters;
  
  // Selection history
  selectionHistory: SelectionHistoryEntry[];
  historyIndex: number;
  maxHistorySize: number;
  
  // Optimistic updates
  pendingUpdates: OptimisticUpdate[];
  
  // Actions
  setWards: (wards: WardInfo[]) => void;
  selectWard: (ward: WardInfo | null) => void;
  selectPlace: (attributes: Record<string, unknown> | null) => void;
  clearSelection: () => void;
  hoverWard: (wardId: number | null) => void;
  updateFilters: (filters: Partial<WardFilters>) => void;
  resetFilters: () => void;
  
  // History actions
  undoSelection: () => void;
  redoSelection: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearHistory: () => void;
  
  // Optimistic update actions
  applyOptimisticUpdate: (wardId: number, changes: Partial<WardInfo>) => void;
  confirmOptimisticUpdate: (wardId: number) => void;
  revertOptimisticUpdate: (wardId: number) => void;
}

const defaultFilters: WardFilters = {
  searchTerm: '',
  sortBy: 'name',
  sortOrder: 'asc',
  selectedDistrict: null,
  areaRange: null,
  populationRange: null,
};

export const useWardStore = create<WardState>((set, get) => ({
  // State
  wards: [],
  selectedWard: null,
  selectedPlaceAttributes: null,
  hoveredWardId: null,
  filters: defaultFilters,
  selectionHistory: [],
  historyIndex: -1,
  maxHistorySize: 50,
  pendingUpdates: [],
  
  // Actions
  setWards: (wards) => set({ wards }),
  
  selectWard: (ward) => {
    const state = get();
    const previousWard = state.selectedWard;
    
    // Add to history
    const newEntry: SelectionHistoryEntry = {
      wardId: ward?.objectid || null,
      timestamp: Date.now(),
    };
    
    // Remove any future history if we're not at the end
    const newHistory = state.selectionHistory.slice(0, state.historyIndex + 1);
    newHistory.push(newEntry);
    
    // Limit history size
    if (newHistory.length > state.maxHistorySize) {
      newHistory.shift();
    }
    
    set({ 
      selectedWard: ward,
      selectedPlaceAttributes: null, // Clear place selection when selecting ward
      selectionHistory: newHistory,
      historyIndex: newHistory.length - 1,
    });
    
    // Emit events
    if (previousWard && previousWard.objectid !== ward?.objectid) {
      eventService.emit(WARD_EVENTS.WARD_DESELECTED, previousWard);
    }
    if (ward) {
      eventService.emit(WARD_EVENTS.WARD_SELECTED, ward);
    }
  },
  
  selectPlace: (attributes) => {
    set({ 
      selectedPlaceAttributes: attributes,
      selectedWard: null, // Clear ward selection when selecting place
    });
  },
  
  clearSelection: () => {
    set({ 
      selectedWard: null,
      selectedPlaceAttributes: null,
    });
  },
  
  hoverWard: (wardId) => {
    set({ hoveredWardId: wardId });
    eventService.emit(WARD_EVENTS.WARD_HOVERED, wardId);
  },
  
  updateFilters: (filters) => set((state) => ({
    filters: { ...state.filters, ...filters }
  })),
  
  resetFilters: () => set({ filters: defaultFilters }),
  
  // History actions
  undoSelection: () => {
    const state = get();
    if (state.historyIndex <= 0) return;
    
    const newIndex = state.historyIndex - 1;
    const previousEntry = state.selectionHistory[newIndex];
    
    const previousWard = previousEntry.wardId 
      ? state.wards.find(w => w.objectid === previousEntry.wardId) || null
      : null;
    
    set({
      selectedWard: previousWard,
      historyIndex: newIndex,
    });
  },
  
  redoSelection: () => {
    const state = get();
    if (state.historyIndex >= state.selectionHistory.length - 1) return;
    
    const newIndex = state.historyIndex + 1;
    const nextEntry = state.selectionHistory[newIndex];
    
    const nextWard = nextEntry.wardId 
      ? state.wards.find(w => w.objectid === nextEntry.wardId) || null
      : null;
    
    set({
      selectedWard: nextWard,
      historyIndex: newIndex,
    });
  },
  
  canUndo: () => {
    const state = get();
    return state.historyIndex > 0;
  },
  
  canRedo: () => {
    const state = get();
    return state.historyIndex < state.selectionHistory.length - 1;
  },
  
  clearHistory: () => set({
    selectionHistory: [],
    historyIndex: -1,
  }),
  
  // Optimistic update actions
  applyOptimisticUpdate: (wardId, changes) => {
    const update: OptimisticUpdate = {
      wardId,
      changes,
      timestamp: Date.now(),
    };
    
    set((state) => ({
      // Add to pending updates
      pendingUpdates: [...state.pendingUpdates, update],
      
      // Apply changes to wards immediately
      wards: state.wards.map((ward) =>
        ward.objectid === wardId
          ? { ...ward, ...changes }
          : ward
      ),
      
      // Update selected ward if it's the one being updated
      selectedWard: state.selectedWard?.objectid === wardId
        ? { ...state.selectedWard, ...changes }
        : state.selectedWard,
    }));
  },
  
  confirmOptimisticUpdate: (wardId) => {
    set((state) => ({
      // Remove from pending updates
      pendingUpdates: state.pendingUpdates.filter(
        (update) => update.wardId !== wardId
      ),
    }));
  },
  
  revertOptimisticUpdate: (wardId) => {
    const state = get();
    const update = state.pendingUpdates.find((u) => u.wardId === wardId);
    
    if (!update) return;
    
    set((prevState) => {
      // Find original ward data
      const originalWard = prevState.wards.find((w) => w.objectid === wardId);
      
      if (!originalWard) return prevState;
      
      // Revert changes by removing the optimistic update properties
      const revertedWard = { ...originalWard };
      Object.keys(update.changes).forEach((key) => {
        delete (revertedWard as Record<string, unknown>)[key];
      });
      
      return {
        // Remove from pending updates
        pendingUpdates: prevState.pendingUpdates.filter(
          (u) => u.wardId !== wardId
        ),
        
        // Revert ward in list
        wards: prevState.wards.map((ward) =>
          ward.objectid === wardId ? originalWard : ward
        ),
        
        // Revert selected ward if it's the one being reverted
        selectedWard: prevState.selectedWard?.objectid === wardId
          ? originalWard
          : prevState.selectedWard,
      };
    });
  },
}));